import * as React from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  Tooltip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
} from "@mui/material";
import { DisplayDateTime } from "components/DisplayDate";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { toast } from "react-toastify";
import { useState } from "react";
import { useRef } from "react";
import { useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFile from "components/Input/UploadFile";
import AttachmentChip from "components/Attachments/AttachmentChip";
import { useDraftAttachmentPaste } from "hooks/useAttachmentUploads";
import { parseAttachmentList, serializeAttachmentList } from "utils/attachmentClipboard";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

export default function NotesModal({
  open,
  handleClose,
  studentId,
  type,
  parentId,
  parentType,
  attachmentsEnabled = false,
}) {
  const isNoteMode = parentId != null && !!parentType;
  const [commentsList, setCommentsList] = useState([]);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState({ attachments: [] });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { isUploading, onPaste: handlePaste } = useDraftAttachmentPaste({
    draft: attachments,
    setDraft: setAttachments,
    enabled: attachmentsEnabled,
  });
  const buttonRef = useRef(null);
  const [{ token, usersList }, {}] = useKothar();

  // Mention functionality states
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearchTerm, setMentionSearchTerm] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [mentionedUserIds, setMentionedUserIds] = useState([]);

  const userDetail = React.useMemo(() => {
    try {
      const raw = localStorage.getItem("userDetail");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const scrollCommentsToBottom = () => {
    const container = buttonRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  };

  const { mutate } = useMutation(postData, {
    onSuccess() {
      fetchComments();
      setText("");
      setAttachments({ attachments: [] });
      setMentionedUserIds([]);
    },
    onError() {
      toast.error("Error Submitting Comment");
    },
  });

  async function postData({ text: noteText, urls }) {
    if (isNoteMode) {
      const marker = urls?.length > 0 ? `[ATTACHMENTS]${urls.join("|")}[/ATTACHMENTS]` : "";
      const formData = new FormData();
      formData.append("parentId", parentId);
      formData.append("parentType", parentType);
      formData.append("noteText", `${noteText?.trim() || ""}${marker}`);
      await axios.post(`${API_URL}/notes`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return;
    }
    await axios.post(`${API_URL}/discussion/${studentId}`, {
      text: noteText,
      type,
      // backend usage differs across screens; send both shapes
      attachments: serializeAttachmentList(urls),
      attachment: serializeAttachmentList(urls),
      mentionedUserIds: mentionedUserIds.length > 0 ? mentionedUserIds : undefined,
    });
  }

  const submitPost = (e) => {
    e.preventDefault();
    const urls = attachmentsEnabled ? attachments?.attachments || [] : [];
    if (!text?.trim() && urls.length === 0) return;
    mutate({ text, urls });
  };

  const parseNoteMarker = (rawText) => {
    const match = rawText?.match(/\[ATTACHMENTS\](.*?)\[\/ATTACHMENTS\]/);
    if (!match) return { text: rawText, attachment: null };
    return { text: rawText.replace(match[0], "").trim(), attachment: match[1] };
  };

  const fetchComments = async () => {
    if (isNoteMode) {
      await axios
        .get(`${API_URL}/notes`, { params: { parentId, parentType } })
        .then((res) => {
          const normalized = (res?.data?.data ?? []).map((n) => {
            const { text: cleanText, attachment: markerAttachment } = parseNoteMarker(n.text);
            return {
              id: n.id,
              text: cleanText,
              attachment: markerAttachment
                ? markerAttachment.split("|").filter(Boolean)
                : n.attachment,
              createdBy: n.createdBy,
              createdDate: n.createdDate,
              commentedById: null,
            };
          });
          setCommentsList(normalized);
          setTimeout(scrollCommentsToBottom, 0);
        })
        .catch((err) => console.log(err));
      return;
    }
    if (!studentId) return;
    await axios
      .get(`${API_URL}/discussion/${studentId}?type=${type}`)
      .then((res) => {
        setCommentsList(res?.data?.comments ?? []);
        setTimeout(scrollCommentsToBottom, 0);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, parentId, parentType]);

  useEffect(() => {
    if (!open) return;
    setText("");
    setAttachments({ attachments: [] });
    setMentionedUserIds([]);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    scrollCommentsToBottom();
  }, [open, commentsList]);

  const extractAttachmentUrls = (comment) =>
    parseAttachmentList(comment?.attachment || comment?.attachments);

  // Filter users based on mention search term
  useEffect(() => {
    if (mentionSearchTerm && usersList) {
      const filtered = usersList.filter(
        (user) =>
          user.name?.toLowerCase().includes(mentionSearchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(mentionSearchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers([]);
    }
  }, [mentionSearchTerm, usersList]);

  const handleTextChange = (e) => {
    const value = e.target.value;
    setText(value);

    // Check for @ symbol
    const lastAtIndex = value.lastIndexOf("@");
    if (lastAtIndex !== -1) {
      const searchTerm = value.substring(lastAtIndex + 1);
      const beforeAt = value.substring(0, lastAtIndex);

      // Check if we're still typing the mention (no space after @)
      if (!searchTerm.includes(" ") && searchTerm.length > 0) {
        setMentionSearchTerm(searchTerm);
        setMentionStartIndex(lastAtIndex);
        setShowMentionDropdown(true);
        setSelectedMentionIndex(0);
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  const handleMentionSelect = (user) => {
    const beforeMention = text.substring(0, mentionStartIndex);
    const afterMention = text.substring(mentionStartIndex + mentionSearchTerm.length + 1);
    const newText = `${beforeMention}@${user.name} ${afterMention}`;

    setText(newText);
    setShowMentionDropdown(false);
    setMentionSearchTerm("");
    setMentionStartIndex(-1);
    setMentionedUserIds((prev) => (prev.includes(user.id) ? prev : [...prev, user.id]));
  };

  const handleKeyDown = (e) => {
    if (showMentionDropdown) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedMentionIndex((prev) => (prev < filteredUsers.length - 1 ? prev + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : filteredUsers.length - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (filteredUsers[selectedMentionIndex]) {
          handleMentionSelect(filteredUsers[selectedMentionIndex]);
        }
      } else if (e.key === "Escape") {
        setShowMentionDropdown(false);
      }
    }
  };

  const deleteIndividualComment = (commentId) => {
    const url = isNoteMode
      ? `${API_URL}/notes/${commentId}?parentType=${parentType}`
      : `${API_URL}/discussion/comment/${commentId}`;
    axios({
      url,
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then(() => {
        toast.success("Note Deleted Successfully");
        fetchComments();
      })
      .catch(() => {
        toast.error("Error Deleting Comment");
      });
  };

  return (
    <React.Fragment>
      <BootstrapDialog onClose={handleClose} open={open} maxWidth={"sm"} fullWidth>
        <form className="m flex flex-col w-full" onSubmit={submitPost}>
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            Notes
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers>
            <div className="min-h-[300px] flex flex-col h-full">
              <div className="flex flex-col justify-between h-full">
                <div className="max-h-[400px] h-full overflow-y-auto" ref={buttonRef}>
                  {commentsList?.length > 0 ? (
                    [...commentsList]
                      .sort((a, b) => {
                        const ai = Number(a?.id);
                        const bi = Number(b?.id);
                        if (Number.isFinite(ai) && Number.isFinite(bi) && ai !== bi) return ai - bi;
                        return new Date(a?.createdDate || 0) - new Date(b?.createdDate || 0);
                      })
                      .map((arg, index) => (
                        <div className="p-1 pr-3" key={arg?.id ?? `comment-${index}`}>
                          {Number(userDetail?.id) === Number(arg?.commentedById) ? (
                            <div className="mb-1 text-right">
                              <div className="flex items-center justify-end gap-1.5 mb-1">
                                <Tooltip title={`ID: ${arg?.commentedById ?? "—"}`} placement="top">
                                  <p className="text-xs text-app-text-muted cursor-default">
                                    {arg?.createdBy} ·{" "}
                                    <DisplayDateTime value={arg?.createdDate} fallback="" />
                                  </p>
                                </Tooltip>
                                <Tooltip title={`ID: ${arg?.commentedById ?? "—"}`} placement="top">
                                  <Avatar
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      fontSize: 11,
                                      bgcolor: "#3b82f6",
                                      cursor: "default",
                                    }}
                                  >
                                    {arg?.createdBy?.charAt(0)?.toUpperCase()}
                                  </Avatar>
                                </Tooltip>
                              </div>
                              <div className="flex items-center justify-end gap-2 cursor-pointer">
                                <Tooltip title="Delete">
                                  <DeleteIcon
                                    sx={{ color: "grey", fontSize: 16 }}
                                    onClick={() => setConfirmDelete(arg?.id)}
                                  />
                                </Tooltip>
                                {arg?.text?.trim() && (
                                  <p className="bg-blue-500 text-white rounded-lg py-1 px-3 inline-block max-w-full text-left whitespace-pre-wrap break-words">
                                    {arg?.text}
                                  </p>
                                )}
                              </div>
                              {extractAttachmentUrls(arg).length > 0 && (
                                <div className="flex flex-wrap gap-2 justify-end mt-2">
                                  {extractAttachmentUrls(arg).map((url) => (
                                    <AttachmentChip key={url} url={url} />
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="mb-2">
                              <div className="flex items-center gap-1.5 mb-1">
                                <Tooltip title={`ID: ${arg?.commentedById ?? "—"}`} placement="top">
                                  <Avatar
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      fontSize: 11,
                                      bgcolor: "#6b7280",
                                      cursor: "default",
                                    }}
                                  >
                                    {arg?.createdBy?.charAt(0)?.toUpperCase()}
                                  </Avatar>
                                </Tooltip>
                                <Tooltip title={`ID: ${arg?.commentedById ?? "—"}`} placement="top">
                                  <p className="text-xs text-app-text-muted cursor-default">
                                    {arg?.createdBy} ·{" "}
                                    <DisplayDateTime value={arg?.createdDate} fallback="" />
                                  </p>
                                </Tooltip>
                              </div>
                              {arg?.text?.trim() && (
                                <p className="bg-app-surface-hover text-app-text rounded-lg py-2 px-4 inline-block max-w-full text-left whitespace-pre-wrap break-words">
                                  {arg?.text}
                                </p>
                              )}
                              {extractAttachmentUrls(arg).length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {extractAttachmentUrls(arg).map((url) => (
                                    <AttachmentChip key={url} url={url} />
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {/* <AlwaysScrollToBottom /> */}
                        </div>
                      ))
                  ) : (
                    <article className="p-6 mb-6 text-base bg-app-surface text-center py-12">
                      No Notes found.
                    </article>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogActions className="py-2 flex flex-col">
            <div className="py-2 px-4 mb-4 bg-app-surface rounded-lg rounded-t-lg border border-app-border w-full relative">
              <label htmlFor="note" className="sr-only">
                Your note
              </label>
              <textarea
                id="note"
                rows="2"
                className="px-0 text-sm text-app-text border-0 focus:ring-0 focus:outline-none w-full"
                placeholder={
                  attachmentsEnabled
                    ? "Write a note... Type @ to mention users, or paste files to attach them"
                    : "Write a note... Type @ to mention users"
                }
                onChange={handleTextChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                value={text}
              ></textarea>

              {/* Mention Dropdown */}
              {showMentionDropdown && filteredUsers.length > 0 && (
                <Paper
                  className="absolute bottom-full left-0 right-0 mb-2 max-h-48 overflow-y-auto z-50"
                  elevation={3}
                >
                  <List dense>
                    {filteredUsers.map((user, index) => (
                      <ListItem
                        key={user.id}
                        button
                        selected={index === selectedMentionIndex}
                        onClick={() => handleMentionSelect(user)}
                        className="hover:bg-app-surface-hover"
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ width: 24, height: 24 }}>
                            {user.name?.charAt(0)?.toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={user.name}
                          secondary={user.email}
                          primaryTypographyProps={{ fontSize: "0.875rem" }}
                          secondaryTypographyProps={{ fontSize: "0.75rem" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </div>

            {attachmentsEnabled && (
              <div className="w-full px-4 mb-3">
                <UploadFile
                  data={attachments}
                  setData={setAttachments}
                  label="Attachments"
                  imageKey="attachments"
                  type="multiple"
                />
              </div>
            )}

            {isUploading && (
              <span className="ml-auto text-xs text-app-text-muted">Uploading pasted files…</span>
            )}
            <Button
              variant="contained"
              type="submit"
              className="ml-auto"
              disabled={
                isUploading ||
                (!text?.trim() &&
                  (!attachments?.attachments || attachments.attachments.length === 0))
              }
            >
              Submit
            </Button>
          </DialogActions>
        </form>
      </BootstrapDialog>
      <Dialog
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle>Delete Note</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this note? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              deleteIndividualComment(confirmDelete);
              setConfirmDelete(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

// const AlwaysScrollToBottom = () => {
//   const elementRef = useRef();
//   useEffect(() => elementRef.current.scrollIntoView());
//   return <div ref={elementRef} />;
// };
